/**
 * Main navigation component for the sidebar
 *
 * This component renders the main navigation items in the sidebar, including:
 * - Regular menu items (Overview, Timeline, etc.)
 * - Dropdown menu items with sub-items or custom components (Chapters, Elements)
 */

import { ContentDropdown } from "@/components/shared/content-dropdown"
import {
    SidebarGroup,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSubButton,
    SidebarMenuSubItem
} from "@/components/ui/sidebar"
import { useProjectMeta } from '@/hooks/meta/useProjectMeta'
import { cn } from "@/lib/utils"
import { useQueryClient } from '@tanstack/react-query'
import { motion } from "framer-motion"
import { type LucideIcon } from "lucide-react"
import { useParams } from 'react-router-dom'

// ... existing interfaces ...

/**
 * Main navigation component for the sidebar
 */
export function NavMain({ items, onNavigate, label }: NavMainProps) {
    const { projectId } = useParams();
    const { currentStoryId } = useProjectMeta(projectId || null);
    const storyId = currentStoryId?.toString();
    const queryClient = useQueryClient();

    // ... existing animation variants ...
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const itemAnim = {
        hidden: { opacity: 0, x: -10 },
        show: { opacity: 1, x: 0 }
    };

    // ... helper render functions ...
    const renderMenuItem = (item: NavItem) => (
        <motion.div variants={itemAnim} key={item.url}>
            <SidebarMenuItem className="">
                <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    onClick={() => onNavigate(item.url)}
                    isActive={item.isActive}
                    className={cn(
                        "h-9 px-3 transition-all duration-300 ease-in-out mb-1.5",
                        "group-data-[collapsible=icon]:!h-9 group-data-[collapsible=icon]:!w-9 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!rounded-md",
                        item.isActive
                            ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm rounded-2xl"
                            : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-2xl"
                    )}
                >
                    <a className="flex items-center gap-2">
                        {item.icon && <item.icon className={cn("size-4", item.isActive ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground")} />}
                        <span className="text-sm">{item.title}</span>
                    </a>
                </SidebarMenuButton>
                {item.action}
            </SidebarMenuItem>
        </motion.div>
    );

    const renderDropdownItem = (item: NavItem) => {
        // Determine if this is a chapters dropdown
        const isChapters = item.title === "Chapters";

        // Handle dropdown open event
        const handleDropdownOpen = () => {
            if (!storyId) return;

            if (isChapters) {
                // Force refetch chapters when dropdown is opened
                queryClient.invalidateQueries({ queryKey: ['content', 'chapters', storyId] });
            }
        };

        return (
            <motion.div variants={itemAnim} key={item.url}>
                <ContentDropdown
                    title={item.title}
                    icon={item.icon || (() => null)}
                    isActive={item.isActive}
                    url={item.url}
                    onNavigate={() => { }}
                    onOpen={handleDropdownOpen}
                    action={item.action}
                >
                    {item.component ? (
                        // Render custom component if provided
                        <item.component />
                    ) : (
                        // Otherwise render sub-items
                        item.items?.map((subItem) => (
                            <SidebarMenuSubItem key={subItem.url} className="px-0 relative">
                                <SidebarMenuSubButton
                                    asChild
                                    onClick={() => onNavigate(subItem.url)}
                                    className="text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors h-9 rounded-lg pl-3"
                                >
                                    <a>
                                        <span className="text-sm">{subItem.title}</span>
                                    </a>
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))
                    )}
                </ContentDropdown>
            </motion.div>
        );
    };

    return (
        <SidebarGroup>
            {label && <SidebarGroupLabel className="px-3">{label}</SidebarGroupLabel>}
            <SidebarMenu>
                <motion.div
                    variants={container}
                    initial="hidden"
                    animate="show"
                    className="space-y-1"
                >

                    {items.map((item) => (
                        // Render either a regular menu item or a dropdown based on the item properties
                        (!item.items && !item.component)
                            ? renderMenuItem(item)
                            : renderDropdownItem(item)
                    ))}
                </motion.div>
            </SidebarMenu>
        </SidebarGroup>
    )
}
